import {CommentsCategory, CommentStatus} from '@domain/comments'
import {useUserContext} from '@hconnect/login'
import {COMMENTS_DATE_FORMAT, formatTimeZoneDate} from '@hconnect/uikit'
import {useUpmPlant} from '@hconnect/upm'
import {useTranslation} from 'react-i18next'

import {useCommentsQuery} from './useCommentsQuery'

import {useUrlParam} from '@refra/modules/routing/hooks'

interface UseFundamentalCommentsParams {
  commentsCategory: CommentsCategory
  commentCategoryInstanceIds: (string | number)[]
}

// "@hm/fundamental" comments support only number ids, but current comments service provides them as strings
export const mapCommentIdForFundamental = (commentId: string) => {
  return commentId as unknown as number
}

export const useFundamentalComments = ({
  commentsCategory,
  commentCategoryInstanceIds
}: UseFundamentalCommentsParams) => {
  const {
    i18n: {language}
  } = useTranslation()

  const plantCode = useUrlParam('plantCode')
  const upmPlant = useUpmPlant({plantId: plantCode})

  const {loggedUser} = useUserContext()
  const currentUserName = loggedUser?.name ?? ''

  const {data: comments} = useCommentsQuery({
    plantCode,
    commentsCategory,
    commentCategoryInstanceIds,
    enabled: commentCategoryInstanceIds.length > 0
  })

  return comments?.map((comment) => {
    const isEdited = comment.status === CommentStatus.Edited

    const name = isEdited ? comment.updatedBy : comment.createdBy
    const date = isEdited ? comment.updatedOn : comment.createdOn

    return {
      id: mapCommentIdForFundamental(comment.commentId),
      date: formatTimeZoneDate(date, upmPlant.timezone, COMMENTS_DATE_FORMAT, language),
      name,
      content: comment.value,
      isEditable: name === currentUserName,
      isDeletable: name === currentUserName,
      commentsCategoryInstanceId: comment.commentsCategoryInstanceId,
      // ownerId is required but not used in the component currently, we do not have ownerId from the backend
      ownerId: comment.createdBy
    }
  })
}
